import {
  getCompanion,
  getItem,
  isFastestMount,
  type ItemTemplate,
  mountSpeedBonus,
  RARITY_COLORS,
  type ShopOffer,
  type ShopPack,
  shopTitleOfOffer,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { paintItemIcon } from './item-icon.js';
import { kitIcon } from './kit.js';
import { dressedIn, type ShopPreviews } from './shop-previews.js';
import { uiFormat, uiPrice, uiText } from './text.js';

/**
 * The shop's cards (ADR 0031): a pack on its stage, another pack in a row,
 * one piece in the grid. They only draw and report clicks; buying and
 * moving between views are the panel's.
 */

export interface ShopCardContext {
  previews: ShopPreviews;
  icons: CssAtlas | null;
  /** What the bag and the body hold, by template id. */
  owned: ReadonlySet<string>;
  /** A checkout is opening: every buy button waits. */
  busy: boolean;
  buy: (offer: ShopOffer) => void;
  openPack: (pack: ShopPack) => void;
}

/** What the ribbon over a pack says. */
const PACK_RIBBONS: Record<ShopPack, string> = {
  founder: 'Édition fondateur',
  dragon: 'Set du dragon',
  scarecrow: 'Édition d’Halloween',
  aurora: 'Aurore de la Compagnie',
  dragonslayer: 'Trophées de wyrm',
  sylvan: 'Set sylvestre',
  bogwitch: 'Set des tourbières',
  djinn: 'Mirages du désert',
  shaman: 'Esprits des pics',
};

/**
 * How large each figure stands on a stage: the character at ×4, every beast
 * drawn to the same 96 px whatever its canvas (the hound's is 32, the
 * horse's 48), so the hound does not tower over the horse and the hero of
 * the picture stays the dressed warrior.
 */
const STAGE_DOLL_SCALE = 4;
const STAGE_BEAST_PX = 96;

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

export function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = uiText(text);
  return node;
}

export function button(className: string, text: string, onClick: () => void): HTMLButtonElement {
  const node = element('button', className, text);
  node.type = 'button';
  node.addEventListener('click', onClick);
  return node;
}

/** Whole percent a bundle saves against its parts bought one by one. */
function savingPercent(offer: ShopOffer): number | undefined {
  if (offer.worthCents === undefined) return undefined;
  return Math.round((1 - offer.priceCents / offer.worthCents) * 100);
}

function heldOf(offer: ShopOffer, owned: ReadonlySet<string>): number {
  return offer.items.filter((item) => owned.has(item.templateId)).length;
}

/** The struck-through worth, the saving and the price, in the class `priceClass`. */
function deal(offer: ShopOffer, className: string, priceClass: string): HTMLElement {
  const row = element('div', className);
  const saving = savingPercent(offer);
  if (offer.worthCents !== undefined) {
    row.append(element('span', 'shop__worth', uiPrice(offer.worthCents)));
  }
  if (saving !== undefined && saving > 0) {
    row.append(element('span', 'shop__save', uiFormat('-{saving} %', { saving })));
  }
  row.append(element('span', priceClass, uiPrice(offer.priceCents)));
  return row;
}

/**
 * A pack's stage: every cosmetic of the offer worn at once by the
 * character, a beast on each side, all cycling the same idle frames.
 */
function stage(offer: ShopOffer, previews: ShopPreviews): HTMLElement {
  const node = element('div', 'shop__stage');
  const beasts: HTMLCanvasElement[] = [];
  for (const item of offer.items) {
    const template = getItem(item.templateId);
    if (template?.companion === undefined) continue;
    const side = getCompanion(template.companion)?.canvas ?? STAGE_BEAST_PX;
    const beast = previews.beast(template, Math.max(1, Math.floor(STAGE_BEAST_PX / side)), 'feet');
    if (beast !== null) beasts.push(beast);
  }
  // Feet on the ground line: the body stands at the bottom of its own canvas.
  const ids = offer.items.map((item) => item.templateId);
  const doll = previews.doll(dressedIn(ids), STAGE_DOLL_SCALE, false);
  // The character in the middle, the beasts split around it.
  const half = Math.ceil(beasts.length / 2);
  node.append(...beasts.slice(0, half), doll, ...beasts.slice(half));
  return node;
}

/** The strip of a pack's pieces: the rarity ring around each, a check on those already held. */
function piecesStrip(offer: ShopOffer, context: ShopCardContext): HTMLElement {
  const strip = element('div', 'shop__pieces');
  for (const item of offer.items) {
    const template = getItem(item.templateId);
    const piece = element('div', 'shop__piece');
    piece.title = uiText(template?.name ?? item.templateId);
    const icon = element('div', `item item--${template?.rarity ?? 'common'}`);
    paintItemIcon(icon, template, context.icons, item.templateId);
    piece.append(icon);
    if (context.owned.has(item.templateId)) {
      piece.classList.add('shop__piece--owned');
      piece.append(element('span', 'shop__check'));
    }
    strip.append(piece);
  }
  return strip;
}

/** The title a pack comes with, on a plate of its own: the blurb alone hid it. */
function titlePerk(offer: ShopOffer): HTMLElement | null {
  const title = shopTitleOfOffer(offer.id);
  if (title === undefined) return null;
  const perk = element('div', 'shop__perk');
  const text = element('div', 'shop__perk-text');
  text.append(
    element('span', 'shop__perk-kicker', 'Titre offert'),
    element('span', 'shop__perk-title', uiFormat('« {title} »', { title: title.title })),
    element('span', 'shop__perk-hint', 'Au-dessus de ton nom, sur tous tes personnages'),
  );
  perk.append(kitIcon('title'), text);
  return perk;
}

/**
 * A pack as a storefront banner: the stage and its pieces on the left, the
 * pitch on the right (ribbon, name, blurb, deal), one large gold button. On
 * the home the strip and a link lead to the pack's page, where the pieces
 * are sold one by one.
 */
export function heroCard(offer: ShopOffer, context: ShopCardContext, onHome: boolean): HTMLElement {
  const pack = offer.pack ?? 'founder';
  const card = element('article', 'shop__hero');
  const show = element('div', 'shop__show');
  const strip = piecesStrip(offer, context);
  if (onHome) {
    strip.classList.add('shop__pieces--link');
    strip.title = uiText('Voir les pièces à l’unité');
    strip.addEventListener('click', () => {
      context.openPack(pack);
    });
  }
  show.append(stage(offer, context.previews), strip);

  const pitch = element('div', 'shop__pitch');
  pitch.append(
    element('span', 'shop__ribbon', PACK_RIBBONS[pack]),
    element('h3', 'shop__hero-name', offer.name),
    element('p', 'shop__hero-blurb', offer.blurb),
  );
  const perk = titlePerk(offer);
  if (perk !== null) pitch.append(perk);
  pitch.append(deal(offer, 'shop__deal', 'shop__hero-price'));
  const cta = button('shop__cta', context.busy ? 'Ouverture…' : 'Obtenir le pack', () => {
    context.buy(offer);
  });
  cta.disabled = context.busy;
  const held = heldOf(offer, context.owned);
  pitch.append(
    cta,
    element(
      'span',
      'shop__note',
      held > 0
        ? uiFormat('Tu en as déjà {held} sur {count} : le pack te les redonne tous.', {
            held,
            count: offer.items.length,
          })
        : perk !== null
          ? uiFormat('{count} objets livrés d’un coup dans ton sac, et le titre.', {
              count: offer.items.length,
            })
          : uiFormat('{count} objets, livrés d’un coup dans ton sac.', {
              count: offer.items.length,
            }),
    ),
  );
  if (onHome) {
    pitch.append(
      button('shop__link', 'Voir les pièces à l’unité', () => {
        context.openPack(pack);
      }),
    );
  }
  card.append(show, pitch);
  return card;
}

/** Another pack, in a row: the dressed character small, name, deal, the way in. */
export function packCard(offer: ShopOffer, context: ShopCardContext): HTMLElement {
  const pack = offer.pack ?? 'founder';
  const card = element('article', 'shop__packcard');
  const preview = element('div', 'shop__preview shop__preview--pack');
  preview.append(context.previews.doll(dressedIn(offer.items.map((item) => item.templateId)), 3));
  const held = heldOf(offer, context.owned);
  const text = element('div', 'shop__packcard-text');
  text.append(
    element('span', 'shop__ribbon', PACK_RIBBONS[pack]),
    element('span', 'shop__packcard-name', offer.name),
    element(
      'span',
      'shop__packcard-meta',
      held > 0
        ? uiFormat('{count} pièces, {held} déjà à toi', { count: offer.items.length, held })
        : uiFormat('{count} pièces, à l’unité ou ensemble', { count: offer.items.length }),
    ),
  );
  const open = button('shop__cta shop__cta--small', 'Voir le pack', () => {
    context.openPack(pack);
  });
  card.append(
    preview,
    text,
    deal(offer, 'shop__deal shop__deal--compact', 'shop__packcard-price'),
    open,
  );
  card.addEventListener('click', (event) => {
    if (event.target instanceof HTMLButtonElement) return;
    context.openPack(pack);
  });
  return card;
}

/** A single piece: alive in its frame, its name, its pitch, one button that carries the price. */
export function pieceCard(offer: ShopOffer, context: ShopCardContext): HTMLElement {
  const card = element('article', 'shop__card');
  const first = getItem(offer.items[0]?.templateId ?? '');

  const preview = element('div', 'shop__preview');
  if (first !== undefined) {
    const alive = context.previews.piece(first);
    if (alive !== null) preview.append(alive);
    else {
      // No frames for it yet: its icon stands in.
      const icon = element('div', `item item--${first.rarity}`);
      paintItemIcon(icon, first, context.icons, first.id);
      preview.append(icon);
    }
    // Owning one is no bar to buying another: a cosmetic is an item like any
    // other, kept, lent or traded, so the card only says what the bag holds.
    if (context.owned.has(first.id)) preview.append(element('span', 'shop__tag', 'Déjà à toi'));
  }

  const name = element('span', 'shop__name', offer.name);
  name.style.color = hex(RARITY_COLORS[first?.rarity ?? 'common']);
  name.title = uiText(offer.name);
  const blurb = element('span', 'shop__blurb', offer.blurb);
  blurb.title = uiText(offer.blurb);
  const speed = first === undefined ? null : speedBadge(first);

  const buy = button('shop__buy', '', () => {
    context.buy(offer);
  });
  buy.disabled = context.busy;
  buy.append(
    element('span', 'shop__buy-label', context.busy ? 'Ouverture…' : 'Acheter'),
    element('span', 'shop__buy-price', uiPrice(offer.priceCents)),
  );

  card.append(preview, name, ...(speed === null ? [] : [speed]), blurb, buy);
  return card;
}

/** A mount's speed on a gold plate: what it is bought for, not left to the blurb. */
function speedBadge(template: ItemTemplate): HTMLElement | null {
  const mount = template.companion === undefined ? undefined : getCompanion(template.companion);
  if (mount?.kind !== 'mount') return null;
  const badge = element('div', 'shop__speed');
  badge.append(
    element(
      'span',
      'shop__speed-value',
      uiFormat('+{bonus} % de vitesse', { bonus: mountSpeedBonus(mount) }),
    ),
  );
  if (isFastestMount(mount)) {
    badge.append(element('span', 'shop__speed-note', 'La monture la plus rapide du jeu'));
  }
  return badge;
}
